import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../utils/api';
import { DashboardLayout } from '../../components/layout';
import { Spinner } from '../../components/ui';
import toast from 'react-hot-toast';

export default function AdminTests() {
  const [batches,  setBatches]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(null); // batch being edited
  const [saving,   setSaving]   = useState(false);
  const [search,   setSearch]   = useState('');

  useEffect(() => {
    adminAPI.getBatches()
      .then(r => setBatches(r.data.batches || []))
      .catch(() => toast.error('Failed to load batches'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateBatch(editing.id, {
        title:            editing.title,
        description:      editing.description,
        total_questions:  editing.total_questions,
        duration_minutes: editing.duration_minutes,
        is_active:        editing.is_active,
      });
      setBatches(prev => prev.map(b => b.id === editing.id ? { ...b, ...editing } : b));
      toast.success('Batch updated!');
      setEditing(null);
    } catch { toast.error('Update failed'); }
    finally { setSaving(false); }
  };

  const filtered = batches.filter(b =>
    search === '' ||
    b.title.toLowerCase().includes(search.toLowerCase()) ||
    String(b.batch_number).includes(search)
  );

  const totalStudents = batches.reduce((s,b) => s + (b.students_completed||0), 0);
  const avgScore = batches.filter(b=>b.avg_score).reduce((s,b,_,a) => s + parseFloat(b.avg_score)/a.length, 0);

  if (loading) return (
    <DashboardLayout title="Batch Management">
      <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:400 }}>
        <Spinner size={40} color="blue" />
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout title="Batch Management 📦" subtitle="100 sequential mock tests — students unlock one by one">

      {/* Stats row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:14, marginBottom:24 }}>
        {[
          { label:'Total Batches',    value: batches.length,                    icon:'📦', color:'var(--blue)' },
          { label:'Active Batches',   value: batches.filter(b=>b.is_active).length, icon:'✅', color:'#28A745' },
          { label:'Avg Score',        value: avgScore ? avgScore.toFixed(1)+'%' : '—', icon:'🎯', color:'#F5A623' },
          { label:'Total Completions',value: totalStudents,                     icon:'🏅', color:'#8B5CF6' },
        ].map(s => (
          <div key={s.label} style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:14, padding:'18px 20px' }}>
            <div style={{ fontSize:24, marginBottom:6 }}>{s.icon}</div>
            <div style={{ fontFamily:'var(--font-heading)', fontWeight:900, fontSize:26, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:12, color:'var(--muted)', marginTop:2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* How it works info box */}
      <div style={{ background:'linear-gradient(135deg,#e8edf9,#f0f4ff)', border:'1px solid var(--blue)', borderRadius:14, padding:'16px 20px', marginBottom:20 }}>
        <div style={{ fontFamily:'var(--font-heading)', fontWeight:700, fontSize:14, color:'var(--blue)', marginBottom:8 }}>
          ℹ️ How the Batch System Works
        </div>
        <div style={{ fontSize:13, color:'var(--text)', lineHeight:1.8 }}>
          1. Student registers + payment approved → <strong>Batch 1 auto-unlocked</strong><br/>
          2. Student completes Batch 1 → <strong>Batch 2 auto-unlocked</strong> immediately<br/>
          3. Each batch = <strong>50 random questions, 60-minute timer</strong><br/>
          4. Progress continues until <strong>Batch 100 (Grand Final) 🏆</strong><br/>
          5. Students can <strong>retry</strong> any completed batch anytime
        </div>
      </div>

      {/* Search */}
      <div style={{ display:'flex', gap:10, marginBottom:16, alignItems:'center' }}>
        <input
          placeholder="Search batch number or title..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding:'9px 14px', borderRadius:10, border:'1px solid var(--border)', fontSize:13, width:260, outline:'none' }}
        />
        <span style={{ fontSize:13, color:'var(--muted)' }}>
          Showing {filtered.length} of {batches.length} batches
        </span>
      </div>

      {/* Batch Table */}
      <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:16, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:'var(--bg)', borderBottom:'2px solid var(--border)' }}>
              {['#','Title','Questions','Duration','Completions','Avg Score','Status','Actions'].map(h => (
                <th key={h} style={{ padding:'12px 14px', textAlign:'left', fontWeight:700, color:'var(--text)', fontSize:12, whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((batch, idx) => (
              <tr key={batch.id} style={{
                borderBottom:'1px solid var(--border)',
                background: batch.batch_number === 100 ? 'linear-gradient(135deg,#FFF9E6,#FFFBF0)' :
                            idx % 2 === 0 ? '#fff' : 'var(--bg)',
              }}>
                <td style={{ padding:'11px 14px', fontFamily:'var(--font-heading)', fontWeight:900, color:'var(--blue)', fontSize:15 }}>
                  {batch.batch_number === 100 ? '🏆' : batch.batch_number}
                </td>
                <td style={{ padding:'11px 14px', fontWeight:600, maxWidth:200 }}>
                  <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{batch.title}</div>
                </td>
                <td style={{ padding:'11px 14px', color:'var(--muted)' }}>{batch.total_questions}</td>
                <td style={{ padding:'11px 14px', color:'var(--muted)' }}>{batch.duration_minutes}m</td>
                <td style={{ padding:'11px 14px' }}>
                  <span style={{ fontFamily:'var(--font-heading)', fontWeight:700, color:'#8B5CF6' }}>
                    {batch.students_completed || 0}
                  </span>
                </td>
                <td style={{ padding:'11px 14px' }}>
                  {batch.avg_score
                    ? <span style={{ fontFamily:'var(--font-heading)', fontWeight:700, color:parseFloat(batch.avg_score)>=60?'#28A745':'#F5A623' }}>{batch.avg_score}%</span>
                    : <span style={{ color:'var(--muted)' }}>—</span>}
                </td>
                <td style={{ padding:'11px 14px' }}>
                  <span style={{
                    background: batch.is_active ? '#e8f5e9' : '#fdecea',
                    color:      batch.is_active ? '#28A745' : '#dc3545',
                    fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:99,
                  }}>
                    {batch.is_active ? '✅ Active' : '❌ Inactive'}
                  </span>
                </td>
                <td style={{ padding:'11px 14px' }}>
                  <button onClick={() => setEditing({ ...batch })}
                    style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8, padding:'5px 12px', cursor:'pointer', fontSize:12, fontWeight:600 }}>
                    ✏️ Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {editing && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16,
        }} onClick={() => setEditing(null)}>
          <div style={{ background:'#fff', borderRadius:18, padding:28, width:'100%', maxWidth:460 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily:'var(--font-heading)', fontWeight:800, fontSize:18, marginBottom:20 }}>
              ✏️ Edit Batch {editing.batch_number}
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <label>
                <div style={{ fontSize:12, fontWeight:700, marginBottom:5, color:'var(--muted)' }}>TITLE</div>
                <input value={editing.title}
                  onChange={e => setEditing(p => ({ ...p, title:e.target.value }))}
                  style={{ width:'100%', padding:'9px 12px', borderRadius:10, border:'1px solid var(--border)', fontSize:13, outline:'none', boxSizing:'border-box' }}
                />
              </label>
              <label>
                <div style={{ fontSize:12, fontWeight:700, marginBottom:5, color:'var(--muted)' }}>DESCRIPTION</div>
                <textarea value={editing.description || ''}
                  onChange={e => setEditing(p => ({ ...p, description:e.target.value }))}
                  rows={2}
                  style={{ width:'100%', padding:'9px 12px', borderRadius:10, border:'1px solid var(--border)', fontSize:13, outline:'none', resize:'vertical', boxSizing:'border-box' }}
                />
              </label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <label>
                  <div style={{ fontSize:12, fontWeight:700, marginBottom:5, color:'var(--muted)' }}>QUESTIONS</div>
                  <input type="number" min={10} max={200} value={editing.total_questions}
                    onChange={e => setEditing(p => ({ ...p, total_questions:parseInt(e.target.value) }))}
                    style={{ width:'100%', padding:'9px 12px', borderRadius:10, border:'1px solid var(--border)', fontSize:13, outline:'none', boxSizing:'border-box' }}
                  />
                </label>
                <label>
                  <div style={{ fontSize:12, fontWeight:700, marginBottom:5, color:'var(--muted)' }}>DURATION (mins)</div>
                  <input type="number" min={10} max={300} value={editing.duration_minutes}
                    onChange={e => setEditing(p => ({ ...p, duration_minutes:parseInt(e.target.value) }))}
                    style={{ width:'100%', padding:'9px 12px', borderRadius:10, border:'1px solid var(--border)', fontSize:13, outline:'none', boxSizing:'border-box' }}
                  />
                </label>
              </div>
              <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
                <input type="checkbox" checked={!!editing.is_active}
                  onChange={e => setEditing(p => ({ ...p, is_active: e.target.checked ? 1 : 0 }))}
                  style={{ width:16, height:16 }}
                />
                <span style={{ fontSize:13, fontWeight:600 }}>Active (visible to students)</span>
              </label>
            </div>
            <div style={{ display:'flex', gap:10, marginTop:20 }}>
              <button className="btn btn-outline" style={{ flex:1 }} onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex:2 }} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : '💾 Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
